import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App.tsx';
import { reconnectNow, setUpdateReady } from './lib/store.ts';
import { toast } from './components/Toast.tsx';
import './styles.css';

// Neue Version: Der Service Worker wartet, bis jemand auf „Neu laden“ klickt (kein Neuladen mitten im Tippen).
// Nach einer Version gesucht wird beim Start, alle 30 Minuten und wenn das Fenster wieder sichtbar wird –
// ein Fenster aus dem Dock bleibt sonst tagelang auf dem alten Stand.
const updateSW = registerSW({
  onNeedRefresh() {
    setUpdateReady(() => void updateSW(true));
    toast('Neue Version der App ist da.', {
      action: { label: 'Neu laden', run: () => updateSW(true) },
      ms: 30_000,
    });
  },
  onRegisteredSW(_url, r) {
    if (!r) return;
    const suchen = () => {
      if (navigator.onLine) void r.update().catch(() => undefined);
    };
    setInterval(suchen, 30 * 60_000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        suchen();
        reconnectNow();
      }
    });
  },
});

// Farbschema aus den Einstellungen (sonst wie das System)
try {
  const t = localStorage.getItem('mm.theme');
  if (t === 'light' || t === 'dark') document.documentElement.dataset['theme'] = t;
} catch {
  /* egal */
}

// Installierte App (eigenes Fenster): Rahmen und runde Ecken (styles.css, html.standalone)
const standalone =
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;
if (standalone) document.documentElement.classList.add('standalone');

// Fensterleiste der installierten App folgt dem Farbschema: theme-color = Seitenfarbe (--bg-page)
const themeColor = () => {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  const farbe = getComputedStyle(document.documentElement).getPropertyValue('--bg-page').trim();
  if (meta && farbe) meta.content = farbe;
};
themeColor();
new MutationObserver(themeColor).observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['data-theme'],
});
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', themeColor);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
);
